import { Components } from "appworks/components/components";
import { GraphicsService } from "appworks/graphics/graphics-service";
import { Layers } from "appworks/graphics/layers/layers";
import { CenterPivot } from "appworks/graphics/pixi/group";
import { gameState } from "appworks/model/game-state";
import { Services } from "appworks/services/services";
import { SoundService } from "appworks/services/sound/sound-service";
import { State } from "appworks/state-machine/states/state";
import { UIFlag, uiFlags } from "appworks/ui/flags/ui-flags";
import { Contract } from "appworks/utils/contracts/contract";
import { Parallel } from "appworks/utils/contracts/parallel";
import { Sequence } from "appworks/utils/contracts/sequence";
import { TweenContract } from "appworks/utils/contracts/tween-contract";
import { Tween } from "appworks/utils/tween";
import { FowlEggPickBonusComponent } from "components/bonus/pick/fowl-egg-pick-bonus-component";
import { FowlBonusTransitionComponent } from "components/fowl-bonus-transition-component";
import { gameLayers } from "game-layers";
import { FowlPickBonusResult } from "model/results/fowl-pick-bonus-result";
import { FowlSoundEvents } from "setup/fowl-sound-events";
import { SlingoFooterComponent } from "slingo/components/slingo-footer-component";
import { SlingoRecord } from "slingo/model/records/slingo-record";
import { SlingoLadderResult } from "slingo/model/results/slingo-ladder-result";
import { MatrixComponent } from "slotworks/components/matrix/matrix-component";
import { FreespinRecord } from "slotworks/model/gameplay/records/freespin-record";

export class FowlEnterPickBonusState extends State {
    public onEnter(): void {
        const eggPickBonus = Components.get(FowlEggPickBonusComponent);
        const result = gameState.getCurrentGame().getLatestResultOfType(FowlPickBonusResult);
        const transitionDuration = 400;

        new Sequence([
            () => this.scatterCelebration(),
            () => Contract.wrap(() => Services.get(SoundService).customEvent(FowlSoundEvents.pick_transition)),
            () => Components.get(FowlBonusTransitionComponent).in(this.getTransitionType()),
            () => gameLayers.BonusUI.defaultScene(),
            () => gameLayers.EggPickBonus.setScene(result.bonusVariant),
            () => gameLayers.BackgroundIntermittentAnimation.setScene("pick"),
            () =>
                Contract.wrap(() => {
                    Layers.setLayerOrder("pickbonus");
                    Components.get(SlingoFooterComponent).clear();
                    uiFlags.set(UIFlag.BONUS, true);
                    Services.get(SoundService).customEvent("start_bonus_music");

                    if (result.bonusVariant === "three_tier") {
                        Services.get(SoundService).customEvent("end_base_music");
                    }
                }),
            () => Components.get(FowlBonusTransitionComponent).out(this.getTransitionType()),
            () => eggPickBonus.enter(result, transitionDuration, transitionDuration),
        ]).then(() => this.complete());
    }

    protected scatterCelebration(): Contract {
        const matrix = Components.get(MatrixComponent);
        const scatters = matrix.getBaseGridSymbols().filter(symbol => symbol.symbolDefinition.scatter);

        const record = gameState.getCurrentGame().getCurrentRecord();
        const result = record.getFirstResultOfType(FowlPickBonusResult);

        if (result.bonusVariant !== "single_tier") {
            return Contract.empty();
        }

        if (record instanceof SlingoRecord) {
            // Base game scatter
            return new Parallel(scatters.map(symbol => () => symbol.win()));
        } else if (record instanceof FreespinRecord) {
            // slot scatter
            const contracts = scatters.map((symbol, index) => {
                const sprite = Services.get(GraphicsService).createSprite("chick_falls");
                CenterPivot(sprite);
                gameLayers.SymbolAnimations.add(sprite);

                const refPosition = gameLayers.SymbolAnimations.getPosition("chick_falls");
                const symbolPosition = gameLayers.MatrixContent.getPosition(`symbol_${symbol.gridPosition.x}_${symbol.gridPosition.y}`);

                sprite.setDualPosition(refPosition);
                sprite.landscape.setPosition(symbolPosition.landscape.getCenterPoint());
                sprite.portrait.setPosition(symbolPosition.portrait.getCenterPoint());

                sprite.visible = false;

                const landscapeTween = new Tween(sprite.landscape).to({ y: sprite.landscape.y + 1250 }, 1500).delay(index * 500);

                const portraitTween = new Tween(sprite.portrait)
                    .to({ y: sprite.portrait.y + 1250 }, 1500)
                    .delay(index * 500)
                    .onStart(() => {
                        sprite.visible = true;
                        Services.get(SoundService).customEvent(FowlSoundEvents.chicken_cluck);
                    })
                    .onComplete(() => sprite.destroy());

                return () => new Parallel([() => TweenContract.wrapTween(landscapeTween), () => TweenContract.wrapTween(portraitTween)]);
            });

            return new Sequence([
                () => matrix.showWinningSymbols(scatters, null),
                () => Contract.wrap(() => matrix.resetSymbols()),
                () => new Parallel(contracts),
            ]);
        }
    }

    protected getTransitionType(): string {
        const gameplay = gameState.getCurrentGame();
        const pickResult = gameplay.getLatestResultOfType(FowlPickBonusResult);
        const ladderResult = gameplay.getLatestResultOfType(SlingoLadderResult);

        if (pickResult.bonusVariant === "single_tier") {
            return "scatters";
        }

        switch (ladderResult.total) {
            case 4:
                return "egg_pick_1";
            case 5:
                return "egg_pick_2";
            case 7:
                return "egg_pick_3";
            case 9:
                return "egg_pick_4";
            case 12:
                return "egg_pick_5";
        }
    }
}
